import React, { useState, useEffect, useRef } from 'react';
import { caUtils } from '@mca/contentapi-js';
import { getUser } from '../services/user';
import { getKeyValues } from '../services/config';
import { getLiveSchedules, setPlaylistResume } from '../services/gvp';
import {
  getIsVspp,
  sendProgress,
  isInsideCDNBuffer,
  onSeekLive,
  getPlaylistDelay,
  toMilliSeconds,
  toSeconds
} from '../services/seek';
import { checkParentalControl } from '../services/parental-control';
import * as logger from '../utils/logger';
import styles from './player-live.css';
import { ProgressBar } from '../components/progress-bar';
import { PlaybackStatus } from '../components/playback-status';
import { useTranslation } from '../hooks/translate';
import { getUrl } from '../utils/spotlight';
import { useUtilsEffectNonInitial } from '../utils/hooks';
import classNames from 'classnames';
import { ERROR_TYPES } from '../constants/errors';
import { ROUTES } from '../constants/routes';

const log = logger.create('Player live');

export function PlayerLive({
  setIsPinRequired,
  handleError,
  refreshPrograms,
  setRoute,
  liveChannel,
  schedules
}) {
  const [translate] = useTranslation();
  const [progress, setProgress] = useState();
  const backToLiveTime = useRef();
  const pauseTime = useRef(0);
  const isFirstPlaying = useRef(true);
  const pauseLimitTimeout = useRef();
  const playerManager = cast.framework.CastReceiverContext.getInstance().getPlayerManager();
  const cdnBuffer = liveChannel.cdnBuffer === -1 ? getKeyValues().cdnBuffer : liveChannel.cdnBuffer;
  const currentSchedule = schedules[0];
  const nextSchedule = schedules[1];

  useEffect(() => {
    playerManager.addEventListener(cast.framework.events.EventType.PAUSE, onPause);

    return () => {
      playerManager.removeEventListener(cast.framework.events.EventType.PAUSE, onPause);
    };
  }, []);

  useEffect(() => {
    playerManager.addEventListener(cast.framework.events.EventType.PAUSE, onPauseLimit);

    return () => {
      playerManager.removeEventListener(cast.framework.events.EventType.PAUSE, onPauseLimit);
      clearTimeout(pauseLimitTimeout.current);
    };
  }, [liveChannel.maxLiveNowGap]);

  // Pause event occurs before seek. Seeking to live from VSPP would cause a seek to right before moving to live.
  useEffect(() => {
    playerManager.addEventListener(cast.framework.events.EventType.LOADED_DATA, onLoad);

    return () => {
      playerManager.removeEventListener(cast.framework.events.EventType.LOADED_DATA, onLoad);
    };
  }, []);

  useEffect(() => {
    isFirstPlaying.current = !!liveChannel.id;
  }, [liveChannel.id]);

  useEffect(() => {
    playerManager.addEventListener(cast.framework.events.EventType.TIME_UPDATE, onTimeUpdate);
    playerManager.addEventListener(cast.framework.events.EventType.PLAYING, onPlaying);

    return () => {
      playerManager.removeEventListener(cast.framework.events.EventType.TIME_UPDATE, onTimeUpdate);
      playerManager.removeEventListener(cast.framework.events.EventType.PLAYING, onPlaying);
    };
  }, [currentSchedule.pid, liveChannel.hasPlaylistExperience, schedules.length]);

  useEffect(() => {
    playerManager.addEventListener(cast.framework.events.EventType.PLAYING, onPlaylistPlaying);

    return () => {
      playerManager.removeEventListener(cast.framework.events.EventType.PLAYING, onPlaylistPlaying);
    };
  }, [liveChannel.id, currentSchedule.pid]);

  useEffect(() => {
    if (currentSchedule?.start && currentSchedule?.end) {
      setProgress((progress) => ({
        ...progress,
        start: new Date(currentSchedule.start).toTimeString().substr(0, 5),
        end: new Date(currentSchedule.end).toTimeString().substr(0, 5)
      }));
    }
  }, [currentSchedule.pid]);

  useEffect(() => {
    if (currentSchedule && isContentBlocked(currentSchedule)) {
      playerManager.stop();
      handleError(ERROR_TYPES.blackout, ERROR_TYPES.blackoutSubtext);
    }
  }, [currentSchedule.pid]);

  // Right after mounting PIN has already been introduced for current schedule (either first load or
  // coming back from route readyToPin).
  useUtilsEffectNonInitial(() => {
    if (currentSchedule?.ageRating >= getUser().minimumAgeRating) {
      setIsPinRequired(true);
      const route = checkParentalControl(true);
      setRoute(route);
    }
  }, [currentSchedule.pid]);

  /**
   * Updates progress information.
   */
  async function onTimeUpdate() {
    if (currentSchedule) {
      if (currentSchedule.start && currentSchedule.end) {
        const timeEmited = toSeconds(Math.round(Date.now() - currentSchedule.start));
        const duration = toSeconds(currentSchedule.end - currentSchedule.start);
        const limitEnd = Math.min((timeEmited * 100) / duration, 100);
        const limitStart =
          timeEmited - cdnBuffer > 0 ? ((timeEmited - cdnBuffer) * 100) / duration : 0;
        const currentTime = playerManager.getAbsoluteTimeForMediaTime(
          playerManager.getCurrentTimeSec()
        );
        const timePlayed = currentTime - toSeconds(currentSchedule.start);
        const value = (Math.max(0, timePlayed) * 100) / duration;

        setProgress((progress) => ({ ...progress, value, limitEnd, limitStart }));
        if (toSeconds(currentSchedule.end) < currentTime) {
          const programs = [...schedules];
          programs.shift();
          refreshPrograms(programs);
          backToLiveTime.current =
            getIsVspp() && !backToLiveTime.current && currentTime + getKeyValues().postPaddingTime;
          // There's no need to set resume of an obsolete schedule since it can't be played from
          // zapping nor first playback.
          if (liveChannel.hasPlaylistExperience && programs[0].end > Date.now()) {
            try {
              await setPlaylistResume({
                itemId: caUtils.getId(programs[0].pid),
                time: toSeconds(Date.now()) - programs[0].start
              });
            } catch (e) {
              log.error('Error sending resume', e);
            }
          }
        }
        if (backToLiveTime.current && currentTime > backToLiveTime.current) {
          backToLiveTime.current = null;
          setRoute(ROUTES.goToLive);
        }
        sendProgress();
      } else {
        try {
          const response = await getLiveSchedules(liveChannel.id);
          refreshPrograms(response);
        } catch {
          // Do nothing
        }
      }
      // If we are running out of programs, ask for more
      if (schedules.length <= 2) {
        try {
          const response = await getLiveSchedules(liveChannel.id);
          refreshPrograms(response);
        } catch (e) {
          // Do nothing
        }
      }
    }
  }

  /**
   * Resume from pause.
   */
  function onPlaying() {
    pauseTime.current &&
      !isInsideCDNBuffer(toMilliSeconds(pauseTime.current)) &&
      !getIsVspp() &&
      onSeekLive(toMilliSeconds(pauseTime.current));
    pauseTime.current = 0;
    clearTimeout(pauseLimitTimeout.current);
  }

  /**
   * Resumes playlist position.
   */
  async function onPlaylistPlaying() {
    if (
      liveChannel.hasPlaylistExperience &&
      !liveChannel.isFastChannel &&
      currentSchedule.pid &&
      isFirstPlaying.current
    ) {
      const delay = toMilliSeconds(getPlaylistDelay());
      if (delay > 0) {
        log.info('Seek delay ms', delay);
        // Seek to delay from beginning of buffer.
        onSeekLive(Date.now() - delay);
        isFirstPlaying.current = false;
      } else {
        log.info('Seek beginning of the schedule');
        onSeekLive(
          currentSchedule.start +
            toMilliSeconds(
              getKeyValues().dashAutoResumeDuration * 6 +
                Math.ceil(playerManager.getStats().maxSegmentDuration)
            ) +
            getKeyValues().svodDelayDash
        );
        try {
          await setPlaylistResume({
            itemId: caUtils.getId(currentSchedule.pid),
            time: toSeconds(Date.now() - currentSchedule.start)
          });
        } catch (e) {
          log.error('Error sending resume', e);
        } finally {
          isFirstPlaying.current = false;
        }
      }
    }
  }

  /**
   * Pause.
   */
  function onPause() {
    pauseTime.current = playerManager.getAbsoluteTimeForMediaTime(
      playerManager.getCurrentTimeSec()
    );
  }

  /**
   * Pause.
   */
  function onPauseLimit() {
    if (getKeyValues().isPauseTimeLimitEnabled && liveChannel.maxLiveNowGap > 0) {
      pauseLimitTimeout.current = setTimeout(() => {
        playerManager.play();
      }, toMilliSeconds(liveChannel.maxLiveNowGap * 60));
    }
  }

  /**
   * Resets pause time mark on new loads.
   */
  function onLoad() {
    pauseTime.current = 0;
    clearTimeout(pauseLimitTimeout.current);
  }

  /**
   * Checks if blackout bar is visible. It will be visible if product contains a blackout
   * @param {Object} product Product to check.
   * @returns {boolean} Whether content is blocked or not.
   */
  function isContentBlocked(product) {
    if (product.forbiddenTechnology !== 3 || !getKeyValues().isBlackoutEnabled) {
      return false;
    }
    const target = product.blackoutTargets.find((el) => el === getUser().blackoutTarget.toString());
    const area =
      product.blackoutAreas.length > 0
        ? getUser().blackoutArea &&
          product.blackoutAreas.find((el) => el === getUser().blackoutArea.toString())
        : product.blackoutAreas.length === 0;
    return target && area;
  }

  /**
   * Creates the text shown in next schedule details with start and end times.
   * @returns {string} Start and End time.
   */
  function getNextScheduleTime() {
    if (nextSchedule) {
      const startTime = new Date(nextSchedule.start).toTimeString().substr(0, 5);
      const endTime = new Date(nextSchedule.end).toTimeString().substr(0, 5);
      return `${startTime} - ${endTime}`;
    }
  }

  /**
   * Obtains minutes left for current schedule.
   * @returns {number} Minutes left.
   */
  function getMinutesLeft() {
    if (currentSchedule) {
      const now = Date.now();
      const minutesLeft = toSeconds(currentSchedule.end - now) / 60;
      const hours = Math.floor(minutesLeft / 60);
      const minutes = Math.round(minutesLeft - hours * 60);
      if (minutesLeft < 1) {
        return translate('remainingSeconds');
      } else if (minutesLeft === 1) {
        return translate('remainingMinute');
      } else if (minutesLeft < 60) {
        return translate('remainingMinutes', { minutes });
      } else {
        return translate('remainingHours', { hours, minutes });
      }
    }
  }

  return (
    <div className={styles.guide}>
      <div className={classNames(styles.logoContainer, styles.bg)}>
        <div className={styles.logoWrapper}>
          <img src={getUrl({ ...liveChannel.logo, width: 67 })} />
        </div>
      </div>
      <div className={classNames(styles.schedule, styles.currentSchedule, styles.bg)}>
        <div className={styles.imageContainer}>
          <div className={styles.imageWrapper}>
            <img src={getUrl({ ...currentSchedule?.image, width: 163 })} />
          </div>
        </div>
        <div className={styles.details}>
          <p className={classNames(styles.text, styles.uppercase, styles.ellipsis)}>
            {translate('current')}
          </p>
          <h1 className={classNames(styles.title, styles.ellipsis)}>{currentSchedule?.title}</h1>
          <p className={classNames(styles.text, styles.ellipsis)}>{getMinutesLeft()}</p>
        </div>
        <div className={styles.progressWrapper}>
          <PlaybackStatus />
          <div className={styles.progress}>
            <ProgressBar {...progress} />
          </div>
        </div>
      </div>
      <div className={classNames(styles.schedule, styles.nextSchedule, styles.bg)}>
        <div className={styles.details}>
          <p className={classNames(styles.text, styles.uppercase, styles.ellipsis)}>
            {translate('next')}
          </p>
          <h1 className={classNames(styles.title, styles.ellipsis)}>{nextSchedule?.title}</h1>
          <p className={classNames(styles.text, styles.ellipsis)}>{getNextScheduleTime()}</p>
        </div>
      </div>
    </div>
  );
}
